import { DEFAULT_ADVANCED_OPTIONS, copyAdvancedOptions } from './advancedOptions'
import { getFirstItem, getLastItem } from './array'
import { removeUndefinedProperties } from './object'

import { LANGUAGES } from '@/utils/constants'

const { PORTUGUESE, ENGLISH, SPANISH } = LANGUAGES

function getAdvanceDefaults(...options) {
  return options.reduce((acc, nextOption) => {
    if (!(nextOption in DEFAULT_ADVANCED_OPTIONS))
      throw new Error(`Unknown option specified: ${nextOption}`)

    return {
      ...acc,
      [nextOption]: DEFAULT_ADVANCED_OPTIONS[nextOption],
    }
  }, {})
}

const DEFAULT_ADVANCED_GROUPS = {
  facialcoding: getAdvanceDefaults('facialcoding', 'enforce_facialcoding'),

  arrayFilter: getAdvanceDefaults('array_filter', 'array_filter_reverse'),

  randomOrder: getAdvanceDefaults('random_order'),

  slider: getAdvanceDefaults(
    'slider_max',
    'slider_min',
    'slider_middlestart',
    'slider_showminmax',
    'slider_layout',
    'slider_accuracy',
  ),

  answersControl: getAdvanceDefaults('max_answers', 'min_answers'),

  extraOptions: getAdvanceDefaults('others'),

  numeric: getAdvanceDefaults('num_value_int_only', 'max_num_value_n', 'min_num_value_n'),

  numeric_ref_validations: getAdvanceDefaults(
    'max_value_reference',
    'min_value_reference',
  ),

  multiNumeric: getAdvanceDefaults('max_sum', 'min_sum', 'sum_equal'),

  prefixSuffix: getAdvanceDefaults('prefix', 'suffix'),
}

const questionSizeToLimeType = {
  small: 'sft',
  medium: 'lft',
  large: 'hft',
}

const DEFAULT_TEXT_LIME_TYPE = questionSizeToLimeType[DEFAULT_ADVANCED_OPTIONS.size]

const {
  facialcoding,
  answersControl,
  arrayFilter,
  extraOptions,
  randomOrder,
  slider,
  numeric,
  multiNumeric,
  numeric_ref_validations,
  prefixSuffix,
} = DEFAULT_ADVANCED_GROUPS

const advancedOrder = [
  'array_filter',
  'array_filter_reverse',
  'advanced_inheritance',
  'cascade_validation',

  'matrix_display',

  'size',
  'enforce_format',

  'display_as_select',
  'display_as_slider',

  'slider_middlestart',
  'slider_showminmax',
  'num_value_int_only',
  'infinite_cards',

  'multiple_evaluation',

  'slider_max',
  'slider_min',
  'slider_accuracy',

  'max_value_reference',
  'max_num_value_n',
  'min_value_reference',
  'min_num_value_n',
  'max_sum',
  'min_sum',
  'sum_equal',

  'maximum_chars',
  'minimum_chars',
  'maximum_word_count',
  'minimum_word_count',

  'display_columns',
  'button_lock_seconds',

  'max_answers',
  'min_answers',

  'others',
  'is_param',
  'prefix',
  'suffix',
  'none_of_the_above',
]

export const FORMULA_TYPES = {
  alternatives: 'alternatives',
}

export const OPERATORS = {
  selected: 'selected',
  notSelected: 'not_selected',
  equals: 'equals',
  notEquals: 'not_equals',
  higherThan: 'higher_than',
  higherOrEqualThan: 'higher_or_equal_than',
  lowerThan: 'lower_than',
  lowerOrEqualThan: 'lower_or_equal_than',
  rankedBelow: 'ranked_below',
  rankedAbove: 'ranked_above',
  contains: 'contains',
  notContains: 'not_contains',
  between: 'between',
}

export const CONDITIONAL_OPERATORS = {
  AND: 'AND',
  OR: 'OR',
}

const formulaGroups = {
  objective: [OPERATORS.selected, OPERATORS.notSelected],

  equality: [OPERATORS.equals, OPERATORS.notEquals],

  numeric: [
    OPERATORS.higherThan,
    OPERATORS.higherOrEqualThan,
    OPERATORS.lowerThan,
    OPERATORS.lowerOrEqualThan,
    OPERATORS.between,
  ],

  text: [OPERATORS.contains, OPERATORS.notContains],

  ranking: [OPERATORS.rankedBelow, OPERATORS.rankedAbove],
}

const validateNumeric = value => {
  const converted = Number(value)

  return {
    isValid: !isNaN(converted),
    value: converted,
  }
}

const addCopyIndicator = (code, indicator) => `${code}${indicator}`

function copyLogc(_logic, _copyIndicator) {
  // this will be updated once we re-vamp our logic section

  return {
    default: 'next',
    conditions: [],
  }
}

export const defaultQuestionCopyExecutor = ({ question, copyIndicator, idControl }) => {
  const { code, advancedOptions, logic, qid } = question

  return {
    ...question,
    code: addCopyIndicator(code, copyIndicator),
    qid: idControl[qid],
    logic: copyLogc(logic, copyIndicator),

    advancedOptions: copyAdvancedOptions(advancedOptions, idControl, code =>
      addCopyIndicator(code, copyIndicator),
    ),
  }
}

export const operatorsConfig = {
  [OPERATORS.selected]: {
    displayText: {
      [PORTUGUESE]: 'Selecionado',
      [ENGLISH]: 'Selected',
      [SPANISH]: 'Seleccionado',
    },
    inputConfig: null,
  },

  [OPERATORS.notSelected]: {
    displayText: {
      [PORTUGUESE]: 'Não Selecionado',
      [ENGLISH]: 'Not Selected',
      [SPANISH]: 'No Seleccionado',
    },
    inputConfig: null,
  },

  [OPERATORS.equals]: {
    displayText: {
      [PORTUGUESE]: 'É Igual a',
      [ENGLISH]: 'Equals',
      [SPANISH]: 'Igual',
    },
    inputConfig: [
      {
        paramProperty: 'value',
        validator: null,
      },
    ],
  },

  [OPERATORS.notEquals]: {
    displayText: {
      [PORTUGUESE]: 'Não é Igual',
      [ENGLISH]: 'Not Equals',
      [SPANISH]: 'no es Igual',
    },
    inputConfig: [
      {
        paramProperty: 'value',
        validator: null,
      },
    ],
  },

  [OPERATORS.higherThan]: {
    displayText: '>',
    inputConfig: [
      {
        paramProperty: 'value',
        validator: validateNumeric,
        type: 'number',
      },
    ],
  },

  [OPERATORS.higherOrEqualThan]: {
    displayText: '>=',
    inputConfig: [
      {
        paramProperty: 'value',
        validator: validateNumeric,
        type: 'number',
      },
    ],
  },

  [OPERATORS.lowerThan]: {
    displayText: '<',
    inputConfig: [
      {
        paramProperty: 'value',
        validator: validateNumeric,
        type: 'number',
      },
    ],
  },

  [OPERATORS.lowerOrEqualThan]: {
    displayText: '<=',
    inputConfig: [
      {
        paramProperty: 'value',
        validator: validateNumeric,
        type: 'number',
      },
    ],
  },

  [OPERATORS.rankedAbove]: {
    displayText: {
      [PORTUGUESE]: 'Ranqueado acima de',
      [ENGLISH]: 'Ranked Above',
      [SPANISH]: 'Ranqueado acima de',
    },
    inputConfig: [
      {
        paramProperty: 'value',
        validator: validateNumeric,
        type: 'number',
      },
    ],
  },

  [OPERATORS.rankedBelow]: {
    displayText: {
      [PORTUGUESE]: 'Ranqueado abaixo de',
      [ENGLISH]: 'Ranked Below',
      [SPANISH]: 'Ranqueado abaixo de',
    },
    inputConfig: [
      {
        paramProperty: 'value',
        validator: validateNumeric,
        type: 'number',
      },
    ],
  },

  [OPERATORS.contains]: {
    displayText: {
      [PORTUGUESE]: 'Contém',
      [ENGLISH]: 'Contains',
      [SPANISH]: 'Contiene',
    },
    inputConfig: [
      {
        paramProperty: 'value',
        validator: null,
      },
    ],
  },

  [OPERATORS.notContains]: {
    displayText: {
      [PORTUGUESE]: 'Não Contém',
      [ENGLISH]: 'Not Contains',
      [SPANISH]: 'No Contiene',
    },
    inputConfig: [
      {
        paramProperty: 'value',
        validator: null,
      },
    ],
  },

  [OPERATORS.between]: {
    displayText: {
      [PORTUGUESE]: 'Entre',
      [ENGLISH]: 'Between',
      [SPANISH]: 'Entre',
    },
    inputConfig: [
      {
        paramProperty: 'low',
        validator: null,
        type: 'number',
        reduced: true,
      },
      {
        paramProperty: 'high',
        validator: null,
        type: 'number',
        joinerString: 'and',
        reduced: true,
      },
    ],
  },
}

export const questionsConfig = {
  radio: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: false,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: true,
      hasFormula: false,
      hasUserInput: true,

      extraOptions: ['onlyRenderWhen'],
    },

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: true,

    canBeReferencedInFlow: true,

    subQuestionConfig: null,

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.objective],
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...randomOrder,

      ...extraOptions,

      ...getAdvanceDefaults(
        'display_columns',
        'is_param',
        'none_of_the_above',
        'display_as_select',
        'display_as_slider',
        'button_lock_seconds',
        'advanced_inheritance',
      ),
    },

    getLimeType: () => 'lr',
  },

  checkbox: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: false,

    canBeReferencedInArrayFilterReverse: true,

    canBeReferencedInFlow: true,

    canBeReferencedInArrayFilter: true,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: true,
      hasMedia: true,
      hasFormula: false,
      hasUserInput: true,
      extraOptions: ['onlyRenderWhen'],
    },

    subQuestionConfig: null,

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...randomOrder,

      ...answersControl,

      ...extraOptions,

      ...getAdvanceDefaults(
        'display_columns',
        'is_param',
        'none_of_the_above',
        'button_lock_seconds',
        'advanced_inheritance',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.objective],
    },

    getLimeType: () => 'mc',
  },

  text: {
    advancedOrder,

    hasAnswer: false,

    hasSubQuestion: false,

    answerConfig: null,

    subQuestionConfig: null,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    advancedOptions: {
      ...facialcoding,

      ...prefixSuffix,

      ...getAdvanceDefaults(
        'size',
        'is_param',
        'maximum_chars',
        'none_of_the_above',
        'enforce_format',
        'minimum_chars',
        'maximum_word_count',
        'minimum_word_count',
        'button_lock_seconds',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.text, ...formulaGroups.equality],
    },

    getLimeType: ({ advancedOptions: { size } }) =>
      questionSizeToLimeType[size] || DEFAULT_TEXT_LIME_TYPE,
  },

  number: {
    advancedOrder,

    hasAnswer: false,

    hasSubQuestion: false,

    answerConfig: null,

    subQuestionConfig: null,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    advancedOptions: {
      ...facialcoding,

      ...numeric,

      ...prefixSuffix,

      ...numeric_ref_validations,

      ...getAdvanceDefaults('is_param', 'none_of_the_above', 'button_lock_seconds'),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.equality, ...formulaGroups.numeric],
    },

    getLimeType: () => 'ni',
  },

  message: {
    advancedOrder,

    hasAnswer: false,

    hasSubQuestion: false,

    answerConfig: null,

    subQuestionConfig: null,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    advancedOptions: {
      ...facialcoding,

      ...getAdvanceDefaults('button_lock_seconds'),
    },

    formulaReferences: null,

    getLimeType: () => 'msg',
  },

  ranking: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: false,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: true,
      hasMedia: false,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen'],
    },

    subQuestionConfig: null,

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...randomOrder,

      ...answersControl,

      ...getAdvanceDefaults(
        'none_of_the_above',
        'button_lock_seconds',
        'advanced_inheritance',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [
        ...formulaGroups.objective,
        ...formulaGroups.ranking,
      ],
    },

    getLimeType: () => 'cls',
  },

  multiText: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: false,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    subQuestionConfig: null,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: true,
      hasMedia: false,
      hasFormula: false,
      hasPrefixSuffix: true,
      extraOptions: ['onlyRenderWhen'],
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...answersControl,

      ...randomOrder,

      ...getAdvanceDefaults(
        'advanced_inheritance',
        'size',
        'display_columns',
        'maximum_chars',
        'minimum_chars',
        'maximum_word_count',
        'minimum_word_count',
        'none_of_the_above',
        'button_lock_seconds',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.text, ...formulaGroups.equality],
    },

    getLimeType: () => 'mft',
  },

  multiNumber: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: false,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    subQuestionConfig: null,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: false,
      hasFormula: false,
      hasPrefixSuffix: true,
      extraOptions: ['onlyRenderWhen'],
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...answersControl,

      ...numeric,

      ...randomOrder,

      ...multiNumeric,

      ...getAdvanceDefaults(
        'advanced_inheritance',
        'display_columns',
        'none_of_the_above',
        'button_lock_seconds',
        'cascade_validation',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.equality, ...formulaGroups.numeric],
    },

    getLimeType: () => 'mni',
  },

  slider: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: false,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    subQuestionConfig: null,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: false,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen', 'leftImage', 'rightImage'],
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...slider,

      ...randomOrder,

      ...multiNumeric,

      ...getAdvanceDefaults(
        'none_of_the_above',
        'button_lock_seconds',
        'advanced_inheritance',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.numeric, ...formulaGroups.equality],
    },

    getLimeType: () => 'mni',
  },

  matrixCheckbox: {
    advancedOrder,

    hasAnswer: true,

    hasSubQuestion: true,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: true,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: true,
      hasMedia: true,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen'],
    },

    subQuestionConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: true,
      extraOptions: ['onlyRenderWhen'],
      hasFormula: false,
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...randomOrder,

      ...answersControl,

      ...getAdvanceDefaults(
        'advanced_inheritance',
        'matrix_display',
        'none_of_the_above',
        'multi_alternative_random',
        'button_lock_seconds',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.objective],
    },

    getLimeType: () => 'arn',
  },

  matrixRadio: {
    advancedOrder,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: true,

    hasAnswer: true,

    hasSubQuestion: true,

    answerConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: true,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen'],
    },

    subQuestionConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: true,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen'],
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...randomOrder,

      ...getAdvanceDefaults(
        'advanced_inheritance',
        'matrix_display',
        'none_of_the_above',
        'multi_alternative_random',
        'button_lock_seconds',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.objective],
    },

    getLimeType: () => 'arr',
  },

  dragAndDrop: {
    advancedOrder,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: false,

    hasAnswer: true,

    hasSubQuestion: true,

    answerConfig: {
      hasFixedRandom: false,
      hasExclusive: false,
      hasMedia: false,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen'],
    },

    subQuestionConfig: {
      hasFixedRandom: true,
      hasExclusive: false,
      hasMedia: true,
      hasFormula: false,
      extraOptions: ['onlyRenderWhen'],
    },

    advancedOptions: {
      ...facialcoding,

      ...arrayFilter,

      ...answersControl,

      ...randomOrder,

      ...getAdvanceDefaults(
        'infinite_cards',
        'button_lock_seconds',
        'advanced_inheritance',
      ),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.objective],
    },

    getLimeType: () => null,
  },

  formula: {
    advancedOrder,

    canBeReferencedInArrayFilter: false,

    canBeReferencedInArrayFilterReverse: false,

    canBeReferencedInFlow: true,

    hasAnswer: true,

    hasSubQuestion: false,

    answerConfig: {
      hasFixedRandom: false,
      hasExclusive: false,
      hasMedia: false,
      hasFormula: true,

      extraOptions: [],
    },

    subQuestionConfig: null,

    advancedOptions: {
      ...getAdvanceDefaults('multiple_evaluation'),
    },

    formulaReferences: {
      [FORMULA_TYPES.alternatives]: [...formulaGroups.objective],
    },

    getLimeType: () => null,

    // this only takes care of question specific things (config inside alternatives in this case)
    // to copy advanced options values, we need each advanced function
    copyQuestion: ({ question, copyIndicator, idControl }) => {
      const { answers, ...defaultCopy } = defaultQuestionCopyExecutor({
        question,
        copyIndicator,
        idControl,
      })

      return {
        ...defaultCopy,
        answers: answers.map(({ conditions, ...answer }) => ({
          ...answer,
          conditions: conditions.map(({ questionRef, ...condition }) => ({
            ...condition,
            questionRef: {
              ...questionRef,

              id: idControl[questionRef.qid] || questionRef.qid,

              code: idControl[questionRef.qid]
                ? addCopyIndicator(questionRef.code)
                : questionRef.code,
            },
          })),
        })),
      }
    },
  },
}

export const formulaTypeToInitialConditionGetter = {
  [FORMULA_TYPES.alternatives]: availableQuestions => {
    const { type, answers, subQuestions, qid, code } = getLastItem(availableQuestions)

    const { hasAnswer, hasSubQuestion, formulaReferences } = questionsConfig[type]

    const questionRef = {
      id: qid,
      code,
      answer: hasAnswer ? `${getFirstItem(answers).code}` : undefined,
      subQuestion: hasSubQuestion ? `${getFirstItem(subQuestions).code}` : undefined,
    }

    const curatedRef = removeUndefinedProperties(questionRef)

    const [operator] = formulaReferences[FORMULA_TYPES.alternatives]

    const condition = {
      questionRef: curatedRef,
      type: FORMULA_TYPES.alternatives,
      params: null,
      operator,
    }

    return condition
  },
}
